import React, {useCallback, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import {BulkActionProgress} from 'src/components/bulk-tool';
import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {STACKOVERFLOW_MESSAGE} from 'src/constants';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {TOOL_PROGRESS_LABEL, TOOL_TASK} from 'src/modules/bulk-update/update-user-group';
import {
    selectUpdateUserGroupTask,
    selectUpdateUserGroupUuid,
    setBackgroundTask,
    setShown,
    setUuid,
} from 'src/modules/bulk-update/update-user-group/update-user-group.slice';
import {deleteGroup} from 'src/openapi/admin/apis/AccountGroupApi';
import {
    assignGroupMemberBulk,
    getUserGroupUpdateProgress,
    stopUserGroupBulkUpdate,
} from 'src/openapi/admin/apis/BulkGroupAssignApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import Button from '@pb/kit-component-button';
import {DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Tooltip} from '@pb/kit-component-popups';

import {GroupMembersTab, GroupSettingsTab, GroupTabs} from '../components';
import {GROUP_TABS} from '../constants';
import {useGetAccountTeamMembers} from '../hooks/useGetAccountTeamMembers';
import {useGetGroupMembers} from '../hooks/useGetGroupMembers';
import {groupsTabMembersActions} from '../slices/groups-tab-members.slice';
import {groupsTabActions} from '../slices/groups-tab.slice';

import {
    DELETE_BUTTON_TOOLTIP,
    DELETE_GROUP_REQUEST_MESSAGE,
    DELETE_GROUP_TASK,
    DELETE_TOOL_NAME,
    EDIT_GROUP_REQUEST_MESSAGE,
    EDIT_GROUP_TASK,
    EDIT_TOOL_NAME,
} from './constants';
import {useEditGroup} from './useEditGroup';

import type {UpdateGroupMembersRequest} from '../typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {MemberInfo} from 'src/openapi/admin/models/MemberInfo';

type Props = {
    accountId: number,
    groupId: number,
    selectedTab: string,
    onCloseModal: (submit?: boolean) => void,
    onDelete: () => void,
    onPendingChange: (pending: boolean) => void,
    showDeleteConfirmation: boolean,
    setShowDeleteConfirmation: (value: boolean) => void,
};

export const EditGroupModal = ({
    accountId, groupId, onPendingChange, onCloseModal, selectedTab,
    onDelete, showDeleteConfirmation, setShowDeleteConfirmation,
}: Props) => {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const {hasAccess} = useAdminAccessRight();

    const createBackgroundTask = useBackgroundTaskManager();
    const [tab, setTab] = useState(selectedTab);

    const {
        isLoading: isAccountTeamMembersLoading,
        loadingError: accountTeamMembersLoadingError,
        data: accountTeamMembers,
    } = useGetAccountTeamMembers({accountId});

    const {
        isLoading: isGroupLoading,
        loadingError: groupLoadingError,
        isChanged: isGroupChanged,
        data: groupData,
        setData: setGroupData,
        saveChanges: saveGroupAction,
    } = useEditGroup({groupId});

    const {
        isLoading: isGroupMembersLoading,
        loadingError: groupMembersLoadingError,
        data: groupMembers,
    } = useGetGroupMembers({groupId});
    const [updateMembersRequest, setUpdateMembersRequest] = useState<UpdateGroupMembersRequest>({});
    const isMembersChanged = useMemo(() => !!(
        updateMembersRequest.addMembers?.length || updateMembersRequest.removeMembers?.length
    ), [updateMembersRequest]);

    const refreshTable = useCallback(() => {
        dispatch(groupsTabActions.requireRefresh());
        dispatch(groupsTabMembersActions.requireRefresh());
    }, [dispatch]);

    const getProgress = useCallback((uuid: string, onDone: (success: boolean) => void) => {
        const backgroundTask = createBackgroundTask(
            TOOL_TASK,
            <BulkActionProgress
                label={TOOL_PROGRESS_LABEL}
                taskSelector={selectUpdateUserGroupTask}
                idSelector={selectUpdateUserGroupUuid}
                getProgress={id => apiDispatch(getUserGroupUpdateProgress({id}))}
                stopAction={id => apiDispatch(stopUserGroupBulkUpdate({id}))}
                setShown={setShown}
                onDone={onDone}
            />,
            {
                maxStack: 1,
                stackOverflowMessage: STACKOVERFLOW_MESSAGE,
            },
        );

        if (backgroundTask) {
            dispatch(setUuid(uuid));
            dispatch(setBackgroundTask(backgroundTask));
        }
    }, [dispatch, apiDispatch, createBackgroundTask]);

    const saveGroup = useCallback(async (options?: ActionOptions) => {

        if (isGroupChanged) {
            const backgroundTask = createBackgroundTask(
                EDIT_GROUP_TASK,
                EDIT_GROUP_REQUEST_MESSAGE, {
                    maxStack: 1,
                    stackOverflowMessage: STACKOVERFLOW_MESSAGE,
                },
            );

            if (backgroundTask) {
                const result = await saveGroupAction();
                if (typeof result === 'string') {
                    backgroundTask.completeFailure(result);
                    return result;
                } else {
                    backgroundTask.completeSuccess(<span>Group <b>{groupData.name}</b> updated</span>);
                }
            }
        }

        if (isMembersChanged) {
            const response = await apiDispatch(assignGroupMemberBulk({
                ...options,
                updateUserGroup: [
                    ...(updateMembersRequest.addMembers || []).map(user => ({
                        userId: user.userId,
                        groupId,
                    })),
                    ...(updateMembersRequest.removeMembers || []).map(user => ({
                        userId: user.userId,
                    })),
                ],
            }));

            if (response.payload.success) {
                getProgress(response.payload.data, refreshTable);
            } else {
                return getError({error: response.payload.data, separator: ' '});
            }
        } else {
            refreshTable();
        }
        onCloseModal(true);
    }, [
        saveGroupAction,
        onCloseModal,
        groupData,
        isGroupChanged,
        isMembersChanged,
        updateMembersRequest,
        getProgress,
        refreshTable,
        apiDispatch,
        groupId,
        createBackgroundTask,
    ]);

    const removeMembers = useCallback(async (onDone: (successful: boolean) => void, options?: ActionOptions) => {
        const response = await apiDispatch(assignGroupMemberBulk({
            ...options,
            updateUserGroup: groupMembers.map(({userId}) => ({userId})),
        }));

        if (response.payload.success) {
            getProgress(response.payload.data, onDone);
        } else {
            return getError({error: response.payload.data, separator: ' '});
        }
    }, [groupMembers, getProgress, apiDispatch]);

    const deleteGroupHandler = useCallback(async (usersRemoved?: boolean) => {
        onDelete();
        const backgroundTask = createBackgroundTask(
            DELETE_GROUP_TASK,
            DELETE_GROUP_REQUEST_MESSAGE, {
                maxStack: 1,
                stackOverflowMessage: STACKOVERFLOW_MESSAGE,
            },
        );

        if (backgroundTask) {
            if (usersRemoved === false) {
                backgroundTask.completeFailure(
                    <span>
                        Failed to delete <b>{groupData.name}</b> group. Group with members can't be deleted.
                    </span>,
                );
            } else {
                const result = await apiDispatch(deleteGroup({groupId}));
                if (result.payload.error) {
                    backgroundTask.completeFailure(getError({
                        error: result.payload.data,
                        separator: ' ',
                    }));
                } else {
                    backgroundTask.completeSuccess(<span>Group <b>{groupData.name}</b> deleted</span>);
                }
            }
        }

        refreshTable();
    }, [groupId, groupData, apiDispatch, createBackgroundTask, onDelete, refreshTable]);

    const removeMembersAndDeleteGroup = useCallback(async (options?: ActionOptions) => {
        if (groupMembers.length) {
            const error = await removeMembers(deleteGroupHandler, options);
            if (error) {
                return error;
            }
        } else {
            deleteGroupHandler();
        }
        onCloseModal(true);
    }, [groupMembers, deleteGroupHandler, removeMembers, onCloseModal]);

    const showConfirmationMessage = useCallback(() => {
        setShowDeleteConfirmation(true);
    }, [setShowDeleteConfirmation]);

    const isChanged = [isGroupChanged, isMembersChanged].some(Boolean);
    const renderedUsers: (MemberInfo & {view: string})[] = useMemo(() => [
        ...(updateMembersRequest.addMembers?.map(user => ({...user, view: 'Added'})) || []),
        ...groupMembers
            .filter(({userId}) => !updateMembersRequest
                .removeMembers?.some(({userId: id}) => id === userId),
            ).map(user => ({...user, view: ''})),
    ], [groupMembers, updateMembersRequest]);

    const canEditGroup = hasAccess(ACCESS_RIGHTS.GROUP_SCREEN_EDIT);
    const canDeleteGroup = hasAccess(ACCESS_RIGHTS.GROUP_SCREEN_DELETE);
    const isActionDisabled = showDeleteConfirmation ? !canDeleteGroup : !canEditGroup || !isChanged;

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                {showDeleteConfirmation ? DELETE_TOOL_NAME : EDIT_TOOL_NAME}
            </DialogHeader>
            <DialogMain style={showDeleteConfirmation ? {padding: 24} : undefined}>
                {showDeleteConfirmation ? (
                    <Group direction="v" gap="s">
                        <Label>Are you sure you want to delete this group?</Label>
                        <div>
                            Deleting the Group will unassign all members from it. The access to searches
                            and lists associated with the Group will be lost for collaborators.
                            <strong> This cannot be undone.</strong>
                        </div>
                    </Group>
                ) : (
                    <Group style={{padding: '0 24px 24px'}} direction="v" gap="s">
                        <GroupTabs currentTab={tab} setCurrentTab={setTab} membersCount={renderedUsers.length}/>
                        {tab === GROUP_TABS.SETTINGS.name && (
                            <GroupSettingsTab
                                accountId={accountId}
                                isLoading={isGroupLoading || isAccountTeamMembersLoading}
                                loadingError={groupLoadingError || accountTeamMembersLoadingError}
                                data={groupData}
                                setData={setGroupData}
                                accountTeamMembers={accountTeamMembers}
                                viewType="Existing"
                                disabled={!canEditGroup}
                            />
                        )}
                        {tab === GROUP_TABS.MEMBERS.name && (
                            <GroupMembersTab
                                accountId={accountId}
                                isLoading={isGroupMembersLoading}
                                loadingError={groupMembersLoadingError}
                                members={groupMembers}
                                renderedUsers={renderedUsers}
                                requestInfo={updateMembersRequest}
                                setRequestInfo={setUpdateMembersRequest}
                                viewType="Existing"
                            />
                        )}
                    </Group>
                )}
            </DialogMain>
            <BulkActionFooter
                onPendingChange={onPendingChange}
                key={showDeleteConfirmation ? 'delete' : 'save'}
                action={showDeleteConfirmation ? 'Delete' : 'Save'}
                onConfirm={showDeleteConfirmation ? removeMembersAndDeleteGroup : saveGroup}
                disabled={isActionDisabled}>
                <Button
                    variant="Type.SECONDARY"
                    onClick={() => {
                        if (showDeleteConfirmation) {
                            setShowDeleteConfirmation(false);
                        } else {
                            onCloseModal(false);
                        }
                    }}>Cancel
                </Button>
                {canDeleteGroup && !showDeleteConfirmation ? (
                    <Tooltip
                        anchor={({anchorRef, getAnchorProps}) => (
                            <Button
                                {...getAnchorProps()}
                                ref={anchorRef}
                                variant="Type.TERTIARY"
                                disabled={showDeleteConfirmation}
                                onClick={groupMembers.length ? showConfirmationMessage : () => {
                                    onCloseModal(true);
                                    deleteGroupHandler();
                                }}>
                                Delete Group
                            </Button>
                        )}
                        placement="top-start">
                        {DELETE_BUTTON_TOOLTIP}
                    </Tooltip>
                ) : null}
            </BulkActionFooter>
        </>
    );
};
